interface ArticleSection {
  id: string
  title: string
  content: string
  order: number
}

interface DocumentationSection {
  id: string
  title: string
  icon: string
  articles: ArticleSection[]
  order: number
}

export const parseTrainingGuide = (markdown: string): DocumentationSection[] => {
  const sections: DocumentationSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: '🚀',
      order: 1,
      articles: [
        {
          id: 'overview',
          title: 'Overview',
          content: extractSection(markdown, '### Overview', '### How to Log In'),
          order: 1
        },
        {
          id: 'login',
          title: 'How to Log In',
          content: extractSection(markdown, 'How to Log In', 'First Things You\'ll See'),
          order: 2
        },
        {
          id: 'first-steps',
          title: 'First Things You\'ll See',
          content: extractSection(markdown, 'First Things You\'ll See', 'Finding Your Way Around'),
          order: 3
        },
        {
          id: 'navigation',
          title: 'Finding Your Way Around',
          content: extractSection(markdown, 'Finding Your Way Around', '## Understanding Your Dashboard'),
          order: 4
        },
        {
          id: 'dashboard',
          title: 'Understanding Your Dashboard',
          content: extractSection(markdown, '## Understanding Your Dashboard', '## Managing Companies'),
          order: 5
        }
      ]
    },
    {
      id: 'sales-quotes',
      title: 'Sales & Quotes',
      icon: '💼',
      order: 2,
      articles: [
        {
          id: 'companies',
          title: 'Managing Companies',
          content: extractSection(markdown, '## Managing Companies', '## Managing Contacts'),
          order: 1
        },
        {
          id: 'contacts',
          title: 'Managing Contacts',
          content: extractSection(markdown, '## Managing Contacts', '## Creating Quotes'),
          order: 2
        },
        {
          id: 'creating-quotes',
          title: 'Creating Quotes',
          content: extractSection(markdown, '## Creating Quotes', '## Quote Approval Process'),
          order: 3
        },
        {
          id: 'approval-process',
          title: 'Quote Approval Process',
          content: extractSection(markdown, '## Quote Approval Process', '### Quick Quote Acceptance (Accept URL)'),
          order: 4
        },
        {
          id: 'accept-url',
          title: 'Quick Quote Acceptance (Phone Sales)',
          content: extractSection(markdown, '### Quick Quote Acceptance (Accept URL)', '### Rejecting a Quote'),
          order: 5
        },
        {
          id: 'rejecting-quotes',
          title: 'Rejecting a Quote',
          content: extractSection(markdown, '### Rejecting a Quote', '### Editing a Quote'),
          order: 6
        },
        {
          id: 'editing-quotes',
          title: 'Editing a Quote',
          content: extractSection(markdown, '### Editing a Quote', '## Converting Quotes to Clients'),
          order: 7
        },
        {
          id: 'converting-clients',
          title: 'Converting Quotes to Clients',
          content: extractSection(markdown, '## Converting Quotes to Clients', '## Onboarding New Clients'),
          order: 8
        }
      ]
    },
    {
      id: 'client-management',
      title: 'Client Management',
      icon: '👥',
      order: 3,
      articles: [
        {
          id: 'onboarding',
          title: 'Onboarding New Clients',
          content: extractSection(markdown, '## Onboarding New Clients', '## Managing Active Clients'),
          order: 1
        },
        {
          id: 'active-clients',
          title: 'Managing Active Clients',
          content: extractSection(markdown, '## Managing Active Clients', '## Invoicing & Payments'),
          order: 2
        },
        {
          id: 'offboarding',
          title: 'Offboarding Clients',
          content: extractSection(markdown, '## Offboarding Clients', '## Reports and Tracking'),
          order: 3
        }
      ]
    },
    {
      id: 'payments',
      title: 'Finance & Payments',
      icon: '💰',
      order: 4,
      articles: [
        {
          id: 'invoice-types',
          title: 'Understanding Invoice Types',
          content: extractSection(markdown, '### Understanding Invoice Types', '### Payment Processing'),
          order: 1
        },
        {
          id: 'payment-processing',
          title: 'Payment Processing',
          content: extractSection(markdown, '### Payment Processing', '### Payment Timeline Summary'),
          order: 2
        },
        {
          id: 'payment-timeline',
          title: 'Payment Timeline Summary',
          content: extractSection(markdown, '### Payment Timeline Summary', '### Best Practices for Payments'),
          order: 3
        },
        {
          id: 'best-practices',
          title: 'Best Practices for Payments',
          content: extractSection(markdown, '### Best Practices for Payments', '### Viewing Invoices'),
          order: 4
        },
        {
          id: 'managing-invoices',
          title: 'Managing Invoices',
          content: extractSection(markdown, '### Viewing Invoices', '## Documents System'),
          order: 5
        }
      ]
    },
    {
      id: 'operations',
      title: 'Operations',
      icon: '📋',
      order: 5,
      articles: [
        {
          id: 'documents',
          title: 'Documents System',
          content: extractSection(markdown, '## Documents System', '## Plans and Pricing'),
          order: 1
        },
        {
          id: 'plans-pricing',
          title: 'Plans and Pricing',
          content: extractSection(markdown, '## Plans and Pricing', '## Reminders'),
          order: 2
        },
        {
          id: 'reminders',
          title: 'Reminders',
          content: extractSection(markdown, '## Reminders', '## Offboarding Clients'),
          order: 3
        },
        {
          id: 'reports',
          title: 'Reports and Tracking',
          content: extractSection(markdown, '## Reports and Tracking', '## What\'s Automated vs Manual'),
          order: 4
        }
      ]
    },
    {
      id: 'automation',
      title: 'Automation & Workflows',
      icon: '🤖',
      order: 6,
      articles: [
        {
          id: 'automated-tasks',
          title: 'What\'s Automated',
          content: extractSection(markdown, '### Fully Automated Tasks', '### Manual Tasks'),
          order: 1
        },
        {
          id: 'manual-tasks',
          title: 'Manual Tasks',
          content: extractSection(markdown, '### Manual Tasks', '## Common Questions'),
          order: 2
        }
      ]
    },
    {
      id: 'reference',
      title: 'Quick Reference',
      icon: 'ℹ️',
      order: 7,
      articles: [
        {
          id: 'common-questions',
          title: 'Common Questions',
          content: extractSection(markdown, '## Common Questions', '## Quick Reference'),
          order: 1
        },
        {
          id: 'quick-reference',
          title: 'Quick Reference',
          content: extractSection(markdown, '## Quick Reference', '## Getting Help'),
          order: 2
        },
        {
          id: 'getting-help',
          title: 'Getting Help',
          content: extractSection(markdown, '## Getting Help', '## Daily Workflow Checklist'),
          order: 3
        },
        {
          id: 'daily-workflow',
          title: 'Daily Workflow Checklist',
          content: extractSection(markdown, '## Daily Workflow Checklist', '## Best Practices'),
          order: 4
        },
        {
          id: 'best-practices',
          title: 'Best Practices',
          content: extractSection(markdown, '## Best Practices', '## Summary: Key Things to Remember'),
          order: 5
        },
        {
          id: 'summary',
          title: 'Key Things to Remember',
          content: extractSection(markdown, '## Summary: Key Things to Remember', '---'),
          order: 6
        }
      ]
    }
  ]

  return sections
}

function extractSection(markdown: string, startMarker: string, endMarker: string): string {
  // For headers starting with ##, ensure we match at the start of a line
  const startPattern = startMarker.startsWith('##') 
    ? '^' + startMarker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    : startMarker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const endPattern = endMarker.startsWith('##') 
    ? '^' + endMarker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    : endMarker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    
  const startRegex = new RegExp(startPattern, 'mi') // multiline flag for ^ to work
  const endRegex = new RegExp(endPattern, 'mi')
  
  const startMatch = markdown.match(startRegex)
  const endMatch = markdown.match(endRegex)
  
  // Debug logging for specific sections
  if (startMarker.includes('Quick Quote Acceptance') || startMarker.includes('Daily Workflow Checklist')) {
    console.log('[Parser] Extracting section:', startMarker)
    console.log('[Parser] Start marker:', startMarker)
    console.log('[Parser] End marker:', endMarker)
    console.log('[Parser] Start match found:', !!startMatch, startMatch?.index)
    console.log('[Parser] End match found:', !!endMatch, endMatch?.index)
    if (startMatch && endMatch) {
      const content = markdown.slice(startMatch.index! + startMatch[0].length, endMatch.index!).trim()
      console.log('[Parser] Extracted content length:', content.length)
      console.log('[Parser] First 200 chars:', content.substring(0, 200))
    } else if (startMatch && !endMatch) {
      // If no end match, extract to end of document
      const content = markdown.slice(startMatch.index! + startMatch[0].length).trim()
      console.log('[Parser] No end marker found, extracted to end:', content.length, 'chars')
    }
  }
  
  if (!startMatch) return ''
  
  const startIndex = startMatch.index! + startMatch[0].length
  const endIndex = endMatch ? endMatch.index! : markdown.length
  
  return markdown.slice(startIndex, endIndex).trim()
}

// Context-aware mapping
export const getContextualHelp = (pathname: string): { sectionId: string; articleId: string } | null => {
  const contextMap: Record<string, { sectionId: string; articleId: string }> = {
    '/dashboard': { sectionId: 'getting-started', articleId: 'dashboard' },
    '/quotes': { sectionId: 'sales-quotes', articleId: 'creating-quotes' },
    '/quotes/create': { sectionId: 'sales-quotes', articleId: 'creating-quotes' },
    '/quotes/pending': { sectionId: 'sales-quotes', articleId: 'approval-process' },
    '/quotes/approved': { sectionId: 'sales-quotes', articleId: 'converting-clients' },
    '/crm/companies': { sectionId: 'sales-quotes', articleId: 'companies' },
    '/crm/contacts': { sectionId: 'sales-quotes', articleId: 'contacts' },
    '/crm/clients': { sectionId: 'client-management', articleId: 'active-clients' },
    '/crm/clients/onboarding': { sectionId: 'client-management', articleId: 'onboarding' },
    '/invoices': { sectionId: 'payments', articleId: 'invoice-types' },
    '/plans-pricing': { sectionId: 'operations', articleId: 'plans-pricing' },
    '/reminders': { sectionId: 'operations', articleId: 'tasks' },
    '/reports': { sectionId: 'operations', articleId: 'reports' },
    '/settings': { sectionId: 'reference', articleId: 'getting-help' }
  }

  // Check for dynamic routes
  if (pathname.includes('/crm/companies/') && pathname.includes('/onboarding')) {
    return { sectionId: 'client-management', articleId: 'onboarding' }
  }
  
  if (pathname.includes('/quotes/') && pathname.includes('/convert-to-client')) {
    return { sectionId: 'sales-quotes', articleId: 'converting-clients' }
  }

  return contextMap[pathname] || null
}
